import { useEffect, useState } from "react";
import { ArrowRight, ArrowDown, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FLOW, DATA_FLOW, SQL_SCHEMA, MONGO_MAP, ESCALATION_PY, ESCALATION_JSX, WIREFRAME, STACK, SECURITY, NEXT_STRUCTURE } from "@/docs/architecture";

const SECTIONS = [["blueprint", "01 Blueprint"], ["schema", "02 Database"], ["prompt", "03 System Prompt"], ["escalation", "04 Eskalasi WA"], ["wireframe", "05 Wireframe KB"], ["stack", "06 Tech Stack & Security"]];

const Code = ({ code, testid }) => (
  <div className="relative">
    <button onClick={() => { navigator.clipboard.writeText(code); toast.success("Disalin"); }} className="absolute right-3 top-3 flex items-center gap-1 border border-white/20 px-2 py-1 font-mono text-[10px] text-slate-300 hover:bg-white/10" data-testid={`${testid}-copy`}><Copy size={10} /> Salin</button>
    <pre className="max-h-[560px] overflow-auto bg-[#0B132B] p-5 text-[12px] leading-relaxed text-slate-200" data-testid={testid}><code>{code}</code></pre>
  </div>
);

const Section = ({ id, n, title, desc, children }) => (
  <section id={id} className="scroll-mt-24 border-t border-slate-200 py-14">
    <p className="eyebrow mb-3">{n}</p>
    <h2 className="text-2xl font-bold tracking-tight text-[#0F2B48] sm:text-3xl">{title}</h2>
    {desc && <p className="mt-2 max-w-3xl text-sm text-slate-600">{desc}</p>}
    <div className="mt-8">{children}</div>
  </section>
);

export default function ArchitectureDocs() {
  const [spec, setSpec] = useState(null);
  useEffect(() => { api.get("/admin/ai-spec").then((r) => setSpec(r.data)); }, []);
  return (
    <div data-testid="architecture-page">
      <div className="bg-[#0B132B] text-white blueprint-grid-dark">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <p className="eyebrow mb-4">Dokumentasi Teknis · v1.0</p>
          <h1 className="max-w-4xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">Arsitektur Sistem CMS & AI Agent PT Rikonstruksi Utama</h1>
          <p className="mt-6 max-w-2xl text-slate-300">Blueprint alur data, skema database, system prompt anti-halusinasi, logika eskalasi WhatsApp, dan wireframe AI Knowledge Base Manager.</p>
          <div className="mt-10 flex flex-wrap gap-2">{SECTIONS.map(([id, l]) => <a key={id} href={`#${id}`} className="border border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-wider hover:border-[#D97706] hover:bg-[#D97706]" data-testid={`docs-nav-${id}`}>{l}</a>)}</div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl">
        <Section id="blueprint" n="01 / Blueprint" title="Alur Kerja Data: CMS → Knowledge Base → AI Agent → Chatbot → WhatsApp" desc="Setiap blok adalah modul independen yang dapat di-scale terpisah (ingestion worker, API, vector store).">
          <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-center" data-testid="docs-flow-diagram">
            {FLOW.map((f, i) => (
              <div key={f.id} className="flex flex-1 flex-col items-center gap-2 lg:flex-row">
                <div className="w-full flex-1 border border-slate-300 bg-white p-4 transition-colors hover:border-[#D97706]">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#0284C7]">{f.tag}</p>
                  <p className="mt-1 text-sm font-bold text-[#0F2B48]">{f.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{f.sub}</p>
                </div>
                {i < FLOW.length - 1 && <><ArrowRight size={16} className="hidden shrink-0 text-[#D97706] lg:block" /><ArrowDown size={16} className="shrink-0 text-[#D97706] lg:hidden" /></>}
              </div>
            ))}
          </div>
          <ol className="mt-10 grid gap-3 md:grid-cols-2">
            {DATA_FLOW.map((s, i) => <li key={i} className="flex gap-4 border-l-2 border-[#0F2B48] bg-white p-4 text-sm"><span className="font-mono text-xs font-bold text-[#D97706]">{String(i + 1).padStart(2, "0")}</span>{s}</li>)}
          </ol>
        </Section>

        <Section id="schema" n="02 / Database Schema" title="PostgreSQL + PGVector (produksi) & Pemetaan MongoDB (MVP berjalan)" desc="Tabel inti: Lead, Chat History, Knowledge Base (dokumen + chunk + embedding), dan Settings. Fungsi match_kb_chunks melakukan hybrid search.">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2"><Code code={SQL_SCHEMA} testid="docs-sql-schema" /></div>
            <div className="border border-slate-200 bg-white p-5">
              <p className="eyebrow mb-4">Koleksi MongoDB (implementasi aktif)</p>
              <div className="space-y-3">{MONGO_MAP.map(([k, v]) => <div key={k}><p className="font-mono text-xs font-bold text-[#0F2B48]">{k}</p><p className="text-xs text-slate-500">{v}</p></div>)}</div>
            </div>
          </div>
        </Section>

        <Section id="prompt" n="03 / System Prompt" title="AI Agent Brain: Persona, Guardrail & Anti-Halusinasi" desc="Prompt ini diambil langsung dari backend yang sedang berjalan — sama persis dengan yang dikirim ke LLM.">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">{spec && <Code code={spec.system_prompt} testid="docs-system-prompt" />}</div>
            <div className="space-y-4">
              <div className="border border-rose-200 bg-rose-50 p-5"><p className="mb-2 flex items-center gap-2 text-sm font-bold text-rose-800"><ShieldCheck size={16} /> Kalimat Penolakan Wajib</p><p className="text-sm text-rose-900" data-testid="docs-refusal-text">“{spec?.refusal_text}”</p></div>
              <div className="border border-slate-200 bg-white p-5 text-xs leading-relaxed text-slate-600">
                <p className="mb-2 text-sm font-bold text-[#0F2B48]">Lapisan pertahanan</p>
                <p>1. Whitelist topik eksplisit di system prompt.</p><p>2. Konteks RAG dibungkus tag &lt;konteks_knowledge_base&gt;.</p><p>3. Larangan mengarang nomor regulasi/harga.</p><p>4. Deteksi penolakan pasca-generate → dicatat sebagai off-topic di analytics.</p><p>5. Instruksi tambahan dapat diatur admin dari CMS.</p>
              </div>
            </div>
          </div>
        </Section>

        <Section id="escalation" n="04 / Eskalasi WhatsApp" title="Logika Pemicu High-Intent & Pesan Otomatis" desc="Skor berbobot: harga, penawaran/MoU, urgensi, dan kirim berkas = 3 poin; minta kontak = 2 poin. Eskalasi aktif bila skor ≥ ambang (default 3, dapat diubah di CMS).">
          <div className="grid gap-6 lg:grid-cols-2">
            <Code code={ESCALATION_PY} testid="docs-escalation-python" />
            <div className="space-y-6">
              <Code code={ESCALATION_JSX} testid="docs-escalation-jsx" />
              {spec && (
                <div className="border border-slate-200 bg-white">
                  <p className="border-b px-4 py-3 text-sm font-bold text-[#0F2B48]">Tabel Pemicu Aktif</p>
                  <table className="w-full text-xs"><tbody className="divide-y">
                    {Object.entries(spec.intent_rules).map(([k, r]) => <tr key={k}><td className="p-3 font-mono">{k}</td><td className="p-3">{r.label}</td><td className="p-3 text-right font-bold text-[#D97706]">+{r.weight}</td></tr>)}
                  </tbody></table>
                </div>
              )}
            </div>
          </div>
        </Section>

        <Section id="wireframe" n="05 / Wireframe" title="Layout UI Dashboard CMS — AI Knowledge Base Manager" desc="Wireframe ini sudah diimplementasikan penuh di /admin/knowledge-base.">
          <pre className="overflow-x-auto border border-slate-300 bg-white p-6 text-[11px] leading-snug text-[#0F2B48]" data-testid="docs-wireframe">{WIREFRAME}</pre>
        </Section>

        <Section id="stack" n="06 / Tech Stack & Security" title="Rekomendasi Stack Produksi vs Implementasi Saat Ini">
          <div className="overflow-x-auto border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="docs-stack-table">
              <thead className="bg-slate-50 text-left font-mono text-[10px] uppercase tracking-widest text-slate-500"><tr><th className="p-3">Layer</th><th className="p-3">Rekomendasi Produksi</th><th className="p-3">MVP Berjalan</th></tr></thead>
              <tbody className="divide-y">{STACK.map(([a, b, c]) => <tr key={a}><td className="p-3 font-semibold text-[#0F2B48]">{a}</td><td className="p-3">{b}</td><td className="p-3 text-slate-500">{c}</td></tr>)}</tbody>
            </table>
          </div>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="border border-slate-200 bg-white p-6"><p className="eyebrow mb-4">Security Checklist</p><ul className="space-y-2 text-sm">{SECURITY.map((s) => <li key={s} className="flex gap-2"><ShieldCheck size={14} className="mt-1 shrink-0 text-emerald-600" />{s}</li>)}</ul></div>
            <Code code={NEXT_STRUCTURE} testid="docs-next-structure" />
          </div>
        </Section>
      </div>
    </div>
  );
}
